import React, { useRef } from 'react';
import { Box, Paper, useTheme, alpha, useMediaQuery, CircularProgress, Alert } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import Layout from '../../components/Layout';
import EventDialog from './components/EventDialog';
import CalendarDayCell from './components/CalendarDayCell';
import { DeleteEventDialog } from './components/DeleteEventDialog';
import dayjs from 'dayjs';
import { CalendarToolbar } from './components/CalendarToolbar';
import { useCalendarHandlers } from './hooks/useCalendarHandlers';

const Calendar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const calendarRef = useRef<FullCalendar>(null);

  const {
    isDialogOpen, setIsDialogOpen,
    selectedEventId, setSelectedEventId,
    dialogInitialData, setInitialData,
    deleteDialogOpen, setDeleteDialogOpen,
    events, loading, error,
    selectedEvent,
    handleDateClick,
    handleEventClick,
    handleSelect,
    handleCreateOrUpdate,
    handleDelete,
    handleDatesSet,
  } = useCalendarHandlers();

  if (loading) return <Layout><CircularProgress /></Layout>;
  if (error) return <Layout><Alert severity="error">Failed to load events: {error.message}</Alert></Layout>;

  return (
    <Layout>
      <CalendarToolbar
        onAddEvent={() => {
          setSelectedEventId(null);
          setInitialData(null);
          setIsDialogOpen(true);
        }}
      />

      {(!events || events.length === 0) ? (
        <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
          No events found for this period.
        </Alert>
      ) : null}

      <Paper sx={{
        p: { xs: 1, sm: 3 },
        borderRadius: 2,
        flexGrow: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        '& .fc-day-today': { backgroundColor: alpha(theme.palette.primary.main, 0.05) },
        '& .fc-event': { cursor: 'pointer' }
      }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={isMobile ? {
            left: 'prev,next',
            center: 'title',
            right: 'dayGridMonth,timeGridDay'
          } : {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          events={events}
          editable={true}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          select={handleSelect}
          datesSet={handleDatesSet}
          views={{
            dayGridMonth: {
              dayCellContent: (arg) => <CalendarDayCell arg={arg} />
            }
          }}
          height="100%"
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short'
          }}
        />
      </Paper>

      <EventDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleCreateOrUpdate}
        onDelete={selectedEventId ? () => setDeleteDialogOpen(true) : undefined}
        title={selectedEventId ? 'Edit Event' : 'Create Event'}
        initialData={selectedEvent ? {
          title: selectedEvent.title,
          description: selectedEvent.description,
          startTime: dayjs(selectedEvent.startTime),
          endTime: dayjs(selectedEvent.endTime),
          location: selectedEvent.location,
          color: selectedEvent.color,
          repeat: selectedEvent.repeat || 'never',
        } : dialogInitialData || undefined}
      />

      <DeleteEventDialog
        open={deleteDialogOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Layout>
  );
};

export default Calendar;
